import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button';
import ModalDialog, { ModalTransition } from '@atlaskit/modal-dialog';
import { B400 } from '@atlaskit/theme/colors';
import styled from '@emotion/styled';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { TRACKING_EVENT_NAMES } from '../utils';

type ModalRedirectConfirmProps = {
  readonly showModal: boolean;
  readonly setShowModal: (showModal: boolean) => void;
  readonly ctaURL: string;
  readonly freeLimit: number;
};

const SIGN_UP = 'SIGN_UP';
const CANCEL = 'CANCEL';

export const ModalRedirectConfirm = ({
  showModal,
  setShowModal,
  ctaURL,
  freeLimit
}: ModalRedirectConfirmProps) => {
  const { createAnalyticsEvent } = useAnalyticsEvents();

  const closeAndRedirect = () => {
    if (createAnalyticsEvent) {
      const analyticsEvent = createAnalyticsEvent({
        name: TRACKING_EVENT_NAMES.unitBreachModalDecision,
        label: 'sign-up-for-free'
      });
      analyticsEvent.fire();
    }

    setShowModal(false);
    window.location.href = ctaURL;
  };

  const cancelAndCloseModal = () => {
    if (createAnalyticsEvent) {
      const analyticsEvent = createAnalyticsEvent({
        name: TRACKING_EVENT_NAMES.unitBreachModalDecision,
        label: 'cancel'
      });
      analyticsEvent.fire();
    }
    setShowModal(false);
  };

  const actions = [
    {
      id: 1,
      name: SIGN_UP,
      text: (
        <FormattedMessage
          id="submitModalLabel"
          defaultMessage="Sign up for Free"
          description="Label for submit button in the modal."
        />
      ),
      onClick: closeAndRedirect
    },
    {
      id: 2,
      name: CANCEL,
      text: (
        <FormattedMessage
          id="cancelModalLabel"
          defaultMessage="Cancel"
          description="Label for cancel button in the modal."
        />
      ),
      onClick: cancelAndCloseModal
    }
  ];

  return (
    <ModalTransition>
      {showModal && (
        <ModalDialog onClose={() => cancelAndCloseModal()} autoFocus={false}>
          <ModalHeader>
            <FormattedMessage
              id="modalHeaderLabel"
              defaultMessage="Free has a {freeLimit}-user limit"
              description="Label for modal header."
              values={{ freeLimit }}
            />
          </ModalHeader>
          <ModalBody>
            <FormattedMessage
              id="modalBodyLabel"
              defaultMessage="If you'd like to add more than {freeLimit} users, consider another plan instead."
              description="Label for modal body."
              values={{ freeLimit }}
            />
          </ModalBody>
          <ModalFooter>
            {actions.map((action) => {
              return (
                <Button
                  key={action.id}
                  appearance={action.name === SIGN_UP ? 'primary' : 'subtle'}
                  onClick={action.onClick}
                >
                  {action.text}
                </Button>
              );
            })}
          </ModalFooter>
        </ModalDialog>
      )}
    </ModalTransition>
  );
};

const ModalHeader = styled.h4`
  font-family: Charlie Display;
  font-size: 20px;
  font-weight: 600;
  line-height: 24px;
  margin: 26px 24px 12px 24px;
`;

const ModalBody = styled.div`
  font-family: Charlie Text;
  font-size: 14px;
  line-height: 20px;
  margin: 0 24px;

  a {
    -webkit-appearance: none;
    color: ${B400};
    outline: none;
    text-decoration: none;
  }
`;

const ModalFooter = styled.div`
  font-family: Charlie Text;
  margin: 24px 24px;
  overflow: auto;

  button {
    float: right;
    line-height: 36px !important;
    margin: 2px 2px 2px 6px;
  }
`;
